import React from 'react';
import { Button, StatusBadge } from '../common';
import { Card } from '../common/Card';
import { Heading, Text, Typography } from '../common/Typography';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatPlayerName } from '../../utils/nameUtils';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';

/**
 * Shows user's personal progress in the tournament
 * For members who are participating
 */
const MyTournamentProgress = ({ 
  userProgress, 
  isSingles = false,
  onViewMatches,
  onViewStandings 
}) => {
  if (!userProgress) return null;

  const { team, matches, completed, wins, losses, nextMatch, groupStanding } = userProgress;
  const winRate = completed > 0 ? Math.round((wins / completed) * 100) : 0;

  return (
    <Card variant="gradient" size="medium" elevated className="border-2 border-primary/30">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="text-2xl">👤</div>
          <Heading level={3} className="text-lg mb-0">
            My Progress
          </Heading>
        </div>
        {groupStanding && (
          <StatusBadge 
            variant={groupStanding.position <= 2 ? 'success' : groupStanding.position <= 4 ? 'primary' : 'default'}
            size="medium"
          >
            {groupStanding.position === 1 ? '🥇' : groupStanding.position === 2 ? '🥈' : groupStanding.position === 3 ? '🥉' : '#' + groupStanding.position}
          </StatusBadge>
        )}
      </div>

      {/* Team/Player name */}
      <div className="mb-4">
        {team.player2 ? (
          <Typography variant="strong" className="text-base">
            <span className={getGenderTextColor(team.player1?.gender || '')}>
              {formatPlayerName(team.player1)}
            </span>
            {' & '}
            <span className={getGenderTextColor(team.player2?.gender || '')}>
              {formatPlayerName(team.player2)}
            </span>
          </Typography>
        ) : (
          <Typography variant="strong" className="text-base">
            <span className={getGenderTextColor(team.player1?.gender || '')}>
              {formatTournamentEntryLabel(team, true)}
            </span>
          </Typography>
        )}
        {groupStanding && (
          <Text className="text-sm text-text-secondary mt-1">
            Group {groupStanding.groupName}
          </Text>
        )}
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-4">
        <div className="text-center p-3 bg-surface/50 rounded-lg backdrop-blur-sm">
          <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">
            {matches}
          </div>
          <Text className="text-xs text-text-muted">
            Matches
          </Text>
        </div>

        <div className="text-center p-3 bg-surface/50 rounded-lg backdrop-blur-sm">
          <div className="text-2xl sm:text-3xl font-bold text-success mb-1">
            {wins}
          </div>
          <Text className="text-xs text-text-muted">
            Wins
          </Text>
        </div>

        <div className="text-center p-3 bg-surface/50 rounded-lg backdrop-blur-sm">
          <div className="text-2xl sm:text-3xl font-bold text-danger mb-1">
            {losses}
          </div>
          <Text className="text-xs text-text-muted">
            Losses
          </Text>
        </div>
      </div>

      {/* Win rate */}
      {completed > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <Text className="text-sm text-text-secondary">Win Rate</Text>
            <Typography variant="strong" className="text-sm">
              {winRate}%
            </Typography>
          </div>
          <div className="w-full bg-surface-subtle rounded-full h-3">
            <div 
              className={`rounded-full h-3 transition-all duration-500 ${
                winRate >= 70 ? 'bg-success' : winRate >= 40 ? 'bg-primary' : 'bg-warning'
              }`}
              style={{ width: `${winRate}%` }}
            />
          </div>
        </div>
      )}

      {/* Group standing details */}
      {groupStanding && (
        <div className="p-3 bg-surface/50 rounded-lg backdrop-blur-sm mb-4">
          <Text className="text-xs text-text-muted mb-2">Group Standings</Text>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <div className="text-lg font-bold text-text-primary">{groupStanding.wins || 0}</div>
              <Text className="text-xs text-text-muted">W</Text>
            </div>
            <div>
              <div className="text-lg font-bold text-text-primary">{groupStanding.losses || 0}</div>
              <Text className="text-xs text-text-muted">L</Text>
            </div>
            <div>
              <div className="text-lg font-bold text-text-primary">{groupStanding.pointsFor || 0}</div>
              <Text className="text-xs text-text-muted">PF</Text>
            </div>
            <div>
              <div className="text-lg font-bold text-text-primary">{groupStanding.pointDifference || 0}</div>
              <Text className="text-xs text-text-muted">+/-</Text>
            </div>
          </div>
        </div>
      )}

      {/* Next match */}
      {nextMatch && (
        <div className="p-3 bg-primary-light/20 border border-primary/30 rounded-lg mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-lg">⚡</div>
            <Text className="text-xs font-medium text-primary">NEXT MATCH</Text>
          </div>
          <Typography variant="strong" className="text-sm">
            {nextMatch.round === 'group' ? 'Group Stage' : 
             nextMatch.round === 'final' ? 'Final' :
             nextMatch.round === 'semi_final' ? 'Semi Final' :
             nextMatch.round === 'quarter_final' ? 'Quarter Final' :
             'Knockout'}
          </Typography>
          <Text className="text-xs text-text-secondary mt-1">
            vs {nextMatch.entry1_id === team.id 
              ? formatTournamentEntryLabel(nextMatch.team2, isSingles)
              : formatTournamentEntryLabel(nextMatch.team1, isSingles)}
          </Text>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {onViewStandings && groupStanding && (
          <Button
            variant="primary"
            size="small"
            onClick={onViewStandings}
            className="flex-1"
          >
            View Standings
          </Button>
        )}
        {onViewMatches && (
          <Button
            variant="secondary"
            size="small"
            onClick={onViewMatches}
            className="flex-1"
          >
            My Matches
          </Button>
        )}
      </div>
    </Card>
  );
};

export default MyTournamentProgress;
